<template>
  <v-dialog
    v-model="canShowDialog"
    max-width="800"
    :transition="false"
    class="aggregator-completing-data-dialog"
    @click:outside="closeDialog"
    @keydown.esc="closeDialog"
  >
    <v-card
      class="v-card"
      style="max-height: 70vh; display: flex; flex-direction: column;"
    >
      <!--    Loader-->
      <v-overlay
        :value="showLoader"
        class="d-flex align-center justify-center"
        opacity="0.2"
        persistent
      >
        <v-progress-circular
          indeterminate
          size="60"
          color="primary"
        ></v-progress-circular>
      </v-overlay>

      <!--      Card title-->
      <v-card-title
        class="v-card-title"
      >
        {{ $t('road-maintenance/aggregator.uploading_document') }}
        <v-spacer/>

        <div
          class="x-image"
        >
          <v-img
            src="/img/icons/x-icon-image.svg"
            contain
            width="30"
            height="30"
            @click="closeDialog"
          />
        </div>
      </v-card-title>

      <!--      Card content-->
      <v-card-text
        class="v-card-text pt-2"
        style="overflow-y: auto;"
      >
        <v-row>
          <v-col>
            <text-field
              v-model="description"
              :field="{
                key: 'description',
                label: $t('road-maintenance/patrol-service.description'),
                editable: true
              }"
              :validation-errors="validationErrors"
              @clear-validation-errors="clearValidationError('description')"
            />
          </v-col>

<!--          Upload attachment btn-->
          <v-col
            :class="validationErrors.document ? 'd-flex flex-column' : ''"
            class="align-content-end"
            :style="{ marginTop: validationErrors.document ? '30px' : '' }"
          >
            <div
              style="display: flex; align-items: flex-end;"
            >
              <universal-button
                type="base"
                :label="$t('road-maintenance/aggregator.upload_attachment')"
                append-icon="mdi-paperclip"
                style="margin-right: 4%;"
                :class="validationErrors.document ? 'upload-btn-red-border' : ''"
                @click="onUploadClick"
              />

<!--              Uploaded file name-->
              <div
                v-if="uploadedFile && uploadedFile.name"
                class="file-name-text"
              >
                {{ uploadedFile.name.length > 10 ? uploadedFile.name.slice(0, 20) + '...' : uploadedFile.name }}
              </div>
            </div>

            <div
              v-if="validationErrors.document"
              style="color: #EF4444;"
            >
              {{ validationErrors.document }}
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col>

          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-row
          style="margin: 0;"
        >
          <v-col
            class="d-flex justify-end"
          >
<!--            Cancel btn-->
            <universal-button
              type="cancel"
              style="align-self: end; font-size: 12px; margin-right: 2%;"
              @click="closeDialog"
            />

<!--            Create new btn-->
<!--            User can create new data from the existing one (user must select one of the filtered data)-->
<!--            or completely create a new one-->
            <universal-button
              type="save"
              style="align-self: end; font-size: 12px;"
              @click="saveData"
            />
          </v-col>
        </v-row>
      </v-card-actions>
      <input
        type="file"
        ref="document_input"
        class="d-none"
        accept="image/jpeg,image/png,application/pdf"
        enctype="multipart/form-data"
        @change="event => onDocumentInput(event)"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import UniversalButton from '@/global/components/buttons/UniversalButton.vue'
import textField from '@/global/components/view-layouts/tabs-layout/components/fields/TextField.vue'
import { api } from '@/global/services/api'

export default {
  name: 'UploadDocumentDialog',

  components: {
    textField,
    UniversalButton
  },

  props: {
    showDialog: {
      type: Boolean,
      default: false
    },
    config: {
      type: Object,
      default: () => ({})
    }
  },

  emits: [
    'close'
  ],

  data () {
    return {
      canShowDialog: false,
      formData: {},
      showLoader: false,
      validationErrors: {},
      description: null,
      uploadedFile: null
    }
  },

  watch: {
    showDialog: {
      immediate: true,
      handler (val) {
        this.canShowDialog = !!val
      }
    }
  },

  methods: {
    closeDialog () {
      this.$emit('close')
    },

    async saveData () {
      try {
        this.showLoader = true
        const formData = new FormData()
        let validationSuccess = true
        const { id: regulationId } = this.$route?.params || {}
        const { fetchTabData } = this.config || {}

        if (!regulationId) {
          validationSuccess = false
        }

        if (!this.description) {
          validationSuccess = false
        }

        if (!this.uploadedFile) {
          this.$set(this.validationErrors, 'document', this.$t('road-maintenance/aggregator.upload_document_warning_message'))
          validationSuccess = false
        }

        if (!validationSuccess) {
          this.showLoader = false
          return
        }

        formData.append('description', this.description)
        formData.append('document', this.uploadedFile)

        await api('road-maintenance').post(`traffic-regulation/${regulationId}/sketch/store`, formData)

        this.showLoader = false
        this.closeDialog()

        if (typeof fetchTabData === 'function') {
          fetchTabData()
        }
      }
      catch (exception) {
        const { errors } = exception?.response?.data || {}
        console.error(exception)
        this.showLoader = false

        if (errors) {
          this.validationErrors = errors
        }
      }
    },

    onUploadClick () {
      this.$refs.document_input.click()
    },

    onDocumentInput (event) {
      try {
        const uploadedFile = event.target.files[0]
        const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'] // Allowed MIME types
        const maxFileSize = 200 * 1024 * 1024 // 200MB in bytes
        const maxFileSizeInMb = 200

        if (uploadedFile && uploadedFile.type && !allowedTypes.includes(uploadedFile.type)) {
          this.pushNotification(`${this.$t('road-maintenance/aggregator.allowed_file_types_message').replace(':types', allowedTypes.join(','))}!`)
          console.warn(`File type not allowed: ${uploadedFile.name} (${uploadedFile.type})`)
          return
        }

        if (uploadedFile.size > maxFileSize) {
          this.pushNotification(`${this.$t('road-maintenance/aggregator.exceeding_file_size_message').replace(':file', uploadedFile?.name || '-').replace(':size', maxFileSizeInMb + 'MB')}!`)
          console.warn(`File size exceeds ${maxFileSizeInMb + 'MB'}: ${uploadedFile.name}`)
          return
        }

        this.uploadedFile = uploadedFile

        if (this.validationErrors.document) {
          this.$delete(this.validationErrors, 'document')
        }
      }
      catch (exception) {
        console.error(exception)
      }
    },

    clearValidationError (key) {
      if (key && this.validationErrors[key]) {
        this.$delete(this.validationErrors, key)
      }
    }
  }
}
</script>

<style scoped lang="scss">
::v-deep.v-dialog__content .v-dialog {
  border-radius: 16px !important;
}

.v-card {
  max-width: 900px;
  border-radius: 16px;
}

.v-card-text {
  background-color: #FFFFFF;
  padding: 20px;
}

.v-card-title {
  font-family: 'Satoshi', sans-serif;
  font-weight: 500 !important;
  font-size: 24px !important;
  line-height: 32px !important;
  color: #111827 !important;
  border-bottom: 1px solid #E5E7EB;
}

.x-image:hover {
  cursor: pointer;
}

.oib-wrapper-col {
  margin-bottom: 10%;
}

.border-right {
  border-right: 1px solid #E5E7EB;
}

.subtitle {
  font-family: 'Satoshi', sans-serif;
  font-weight: 500 !important;
  font-size: 20px !important;
  line-height: 28px !important;
  color: #111827 !important;
  margin-bottom: 10%;
}

.file-name-text {
  font-family: 'Satoshi', sans-serif;
  font-weight: 600 !important;
  font-size: 16px !important;
  line-height: 24px !important;
  color: #111827 !important;
}

.upload-btn-red-border {
  border: 1px solid #EF4444 !important;
}
</style>
